import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import CycleDP from "layouts/dashboard/components/CycleDP";
import Activities from "layouts/dashboard/components/Summary/Activities";
import MortalityChart from "examples/Charts/LineCharts/ReportsLineChart/MortalityChart";
import EnvChart from "examples/Charts/LineCharts/ReportsLineChart/EnvChart";
import LuxChart from "examples/Charts/LineCharts/ReportsLineChart/LuxChart";
import FloorTempChart from "examples/Charts/LineCharts/ReportsLineChart/FloorTempChart";
import WaterChart from "examples/Charts/LineCharts/ReportsLineChart/WaterChart";
import WaterQChart from "examples/Charts/LineCharts/ReportsLineChart/WaterQChart";
import WeightChart from "examples/Charts/BarCharts/ReportsBarChart/WeightChart";
import FeedChart from "examples/Charts/BarCharts/ReportsBarChart/FeedChart";
import { useLocation } from "react-router-dom";
import {
  farmsForUserUrl,
  allBroilersUrl,
  mortalityUrl,
  weightUrl,
  envUrl,
  luxUrl,
  floorTempUrl,
  cardSummaryUrl,
  feedUrl,
  waterUrl,
  waterQualityUrl,
  dashboardSummaryMetricsUrl,
  avgTempUrl,
  avgHumUrl,
  avgFloorTempUrl,
  avgNH3Url,
  avgCyclePerformanceKpiUrl,
  avgLuxUrl,
  avgDarkUrl,
  avgWaterTempUrl,
  avgWaterPhUrl,
  avgActivityUrl,
  broilersByFarmUrl,
} from "api/endpoints";
import { buildMortalityChartData } from "utils/mortalityChart";
import { saveMortalityRedirect } from "utils/reportNavigation";

const DASHBOARD_CACHE_KEY = "OmnioBroilerDashboardCache";
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

const canUseBrowserStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readDashboardCache = () => {
  if (!canUseBrowserStorage()) return null;
  const raw = window.localStorage.getItem(DASHBOARD_CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      if (!parsed.global || typeof parsed.global !== "object") {
        parsed.global = {};
      }
      if (!parsed.facilityData || typeof parsed.facilityData !== "object") {
        parsed.facilityData = {};
      }
      return parsed;
    }
  } catch (error) {
    console.warn("Unable to parse dashboard cache", error);
  }
  return { global: {}, facilityData: {} };
};

const writeDashboardCache = (cache) => {
  if (!canUseBrowserStorage() || !cache) return;
  try {
    window.localStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn("Unable to persist dashboard cache", error);
  }
};

const ensureDashboardCacheShape = (cache) => {
  if (!cache || typeof cache !== "object") {
    return { global: {}, facilityData: {}, timestamp: Date.now() };
  }
  const next = { ...cache };
  if (!next.global || typeof next.global !== "object") {
    next.global = {};
  }
  if (!next.facilityData || typeof next.facilityData !== "object") {
    next.facilityData = {};
  }
  if (!next.timestamp) {
    next.timestamp = Date.now();
  }
  return next;
};

const createSkipAuthConfig = () => ({ omnioOffline: { skipAuthHeader: true } });

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const STORAGE_KEY = "facilityLandingSelections";
const createEmptyChartState = () => ({
  labels: [],
  datasets: [],
});
const DASHBOARD_POLL_INTERVAL_MS = 0;
const startDashboardPolling = (callback) => {
  if (!Number.isFinite(DASHBOARD_POLL_INTERVAL_MS) || DASHBOARD_POLL_INTERVAL_MS <= 0) {
    return null;
  }
  return window.setInterval(callback, DASHBOARD_POLL_INTERVAL_MS);
};
const stopDashboardPolling = (intervalId) => {
  if (intervalId !== null) {
    window.clearInterval(intervalId);
  }
};

const INITIAL_CARD_DATA = {
  count: 0,
  cycle: "loading...",
  cycle_day: "�",
  start_date: "�",
  timestamp: "",
  health: "neutral",
};

const INITIAL_AVG_TEMP = { avg_temp_dp: null, avg_tempdp_error: false };
const INITIAL_AVG_HUM = { avg_hum_dp: null, avg_humdp_error: false };
const INITIAL_AVG_NH3 = { avg_nh3_dp: null, avg_nh3dp_error: false };
const INITIAL_AVG_LUX = { avg_lux_dp: null, avg_luxdp_error: false };
const INITIAL_AVG_DARK = { avg_dark_dp: null, avg_darkdp_error: false };
const INITIAL_AVG_KPI = {
  avg_fcr_dp: "loading...",
  avg_weight_dp: "loading...",
  avg_mortality_dp: "loading...",
  avg_fcrdp_error: false,
  avg_weightdp_error: false,
  avg_mortalitydp_error: false,
};
const INITIAL_AVG_WATER_TEMP = { avg_wtemp_dp: null, avg_wtempdp_error: false };
const INITIAL_AVG_ACT = { avg_act_dp: "loading...", avg_actdp_error: false };
const INITIAL_AVG_FTEMP = { avg_ftemp_dp: null, avg_ftempdp_error: false };
const INITIAL_DASHBOARD_SUMMARY_METRICS = {
  rateMortality: null,
  mortality: null,
  culls: null,
  liveBirds: null,
  avgWeight: null,
  totalWeight: null,
  fcr: null,
  totalFeed: null,
  avgWater: null,
  totalWater: null,
};
const getFirstResponseRecord = (data) => {
  if (Array.isArray(data)) return data[0] || {};
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed[0] || {} : parsed || {};
    } catch (error) {
      return {};
    }
  }
  return data || {};
};
const normalizeAverageDeviationPayload = (payload, valueKey, errorKey) => {
  const next = { ...getFirstResponseRecord(payload), [errorKey]: false };
  const numericValue = Number(next[valueKey]);
  next[valueKey] = Number.isFinite(numericValue) ? numericValue : null;
  return next;
};
const resolveCycleId = (data) => {
  const value = data?.cycleId ?? data?.cycleID ?? data?.CycleID ?? data?.cycle_id ?? data?.Cycle_ID;
  if (value === undefined || value === null) return null;
  const normalized = `${value}`.trim();
  if (!normalized || normalized === "loading..." || normalized === "null") return null;
  return normalized;
};
const unwrapResponseRecord = (data) => {
  const record = getFirstResponseRecord(data);
  if (typeof record?.body === "string") {
    try {
      return getFirstResponseRecord(JSON.parse(record.body));
    } catch (error) {
      return record;
    }
  }
  if (record?.body && typeof record.body === "object") {
    return getFirstResponseRecord(record.body);
  }
  return record;
};
const parseNullableNumber = (value) => {
  if (value === null || value === undefined || value === "" || value === "null") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};
const pickNullableNumber = (source, keys) => {
  if (!source || typeof source !== "object") return null;
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return parseNullableNumber(source[key]);
    }
  }
  return null;
};
const formatDashboardSummaryMetric = (value, suffix = "") => {
  const numeric = parseNullableNumber(value);
  if (numeric === null) return "--";
  const formatted = numeric.toLocaleString(undefined, {
    maximumFractionDigits: 3,
  });
  return `${formatted}${suffix}`;
};
const padDashboardDetailLabel = (label) => {
  return `${label}: `;
};

function Dashboard() {
  const query = useQuery();
  const facilityID = query.get("facility");
  const navigate = useNavigate();

  const cacheRef = useRef(ensureDashboardCacheShape(readDashboardCache()));
  const isMountedRef = useRef(true);
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine === false
  );

  const ensureCache = useCallback(() => {
    const current = ensureDashboardCacheShape(cacheRef.current);
    cacheRef.current = current;
    return current;
  }, []);

  const updateGlobalCache = useCallback(
    (partial) => {
      if (!partial) return;
      const cache = ensureCache();
      cache.global = { ...(cache.global || {}), ...partial, lastUpdated: Date.now() };
      cache.timestamp = Date.now();
      cacheRef.current = cache;
      writeDashboardCache(cache);
    },
    [ensureCache]
  );

  const updateFacilityCache = useCallback(
    (facilityId, partial) => {
      if (!facilityId || !partial) return;
      const cache = ensureCache();
      const existing = cache.facilityData[facilityId] || {};
      cache.facilityData[facilityId] = {
        ...existing,
        ...partial,
        lastUpdated: Date.now(),
      };
      cache.timestamp = Date.now();
      cacheRef.current = cache;
      writeDashboardCache(cache);
    },
    [ensureCache]
  );

  const getCachedGlobal = useCallback(() => {
    const cache = ensureCache();
    return cache.global || {};
  }, [ensureCache]);

  const getCachedFacility = useCallback(
    (targetFacilityId) => {
      if (!targetFacilityId) return null;
      const cache = ensureCache();
      return cache.facilityData?.[targetFacilityId] || null;
    },
    [ensureCache]
  );

  const applyCachedGlobal = useCallback(
    (key, setter) => {
      const cached = getCachedGlobal();
      if (cached && Object.prototype.hasOwnProperty.call(cached, key)) {
        setter(cached[key]);
      }
    },
    [getCachedGlobal]
  );

  const applyCachedFacilitySection = useCallback(
    (targetFacilityId, key, setter) => {
      const cached = getCachedFacility(targetFacilityId);
      if (cached && Object.prototype.hasOwnProperty.call(cached, key)) {
        setter(cached[key]);
      }
    },
    [getCachedFacility]
  );

  const hydrateGlobalFromCache = useCallback(() => {
    applyCachedGlobal("farms", (value) => {
      if (Array.isArray(value)) {
        setFarms(value);
      }
    });
    applyCachedGlobal("allFacilities", (value) => {
      if (Array.isArray(value)) {
        setAllFacilities(value);
      }
    });
    applyCachedGlobal("selectedFarmId", (value) => {
      if (value !== undefined) {
        setSelectedFarmId(value || "");
      }
    });
  }, [applyCachedGlobal]);

  const hydrateFacilityFromCache = useCallback(
    (targetFacilityId, options = {}) => {
      if (!targetFacilityId) return;
      const { includeAvgDP = false } = options;
      applyCachedFacilitySection(targetFacilityId, "lineChartDataMortality", (value) => {
        if (value) setLineChartDataMortality(value);
      });
      applyCachedFacilitySection(targetFacilityId, "barChartDataWeight", (value) => {
        if (value) setBarChartDataWeight(value);
      });
      applyCachedFacilitySection(targetFacilityId, "envChartData", (value) => {
        if (value) setEnvChartData(value);
      });
      applyCachedFacilitySection(targetFacilityId, "feedChartData", (value) => {
        if (value) setFeedChartData(value);
      });
      applyCachedFacilitySection(targetFacilityId, "waterChartData", (value) => {
        if (value) setWaterChartData(value);
      });
      applyCachedFacilitySection(targetFacilityId, "waterqChartData", (value) => {
        if (value) setWaterQChartData(value);
      });
      applyCachedFacilitySection(targetFacilityId, "luxChartData", (value) => {
        if (value) setLuxChartData(value);
      });
      applyCachedFacilitySection(targetFacilityId, "ftempChartData", (value) => {
        if (value) setFtempChartData(value);
      });
      applyCachedFacilitySection(targetFacilityId, "cardData", (value) => {
        if (value) setCardData(value);
      });
      applyCachedFacilitySection(targetFacilityId, "dashboardSummaryMetrics", (value) => {
        if (value) setDashboardSummaryMetrics(value);
      });
      if (includeAvgDP) {
        applyCachedFacilitySection(targetFacilityId, "avgTempDP", (value) => {
          if (value) setAvgTempDP(value);
        });
        applyCachedFacilitySection(targetFacilityId, "avgHumDP", (value) => {
          if (value) setAvgHumDP(value);
        });
        applyCachedFacilitySection(targetFacilityId, "avgNH3DP", (value) => {
          if (value) setAvgNH3DP(value);
        });
        applyCachedFacilitySection(targetFacilityId, "avgLuxDP", (value) => {
          if (value) setAvgLuxDP(value);
        });
        applyCachedFacilitySection(targetFacilityId, "avgDarkDP", (value) => {
          if (value) setAvgDarkDP(value);
        });
        applyCachedFacilitySection(targetFacilityId, "avgKPIDP", (value) => {
          if (value) setAvgKPIDP(value);
        });
        applyCachedFacilitySection(targetFacilityId, "avgWaterTempDP", (value) => {
          if (value) setAvgWaterTempDP(value);
        });
        applyCachedFacilitySection(targetFacilityId, "avgActDP", (value) => {
          if (value) setAvgActDP(value);
        });
        applyCachedFacilitySection(targetFacilityId, "avgFTempDP", (value) => {
          if (value) setAvgFTempDP(value);
        });
      }
    },
    [applyCachedFacilitySection]
  );

  const applyFacilityCached = useCallback(
    (sectionKey, setter) => {
      if (!facilityID) return;
      applyCachedFacilitySection(facilityID, sectionKey, (value) => {
        if (value !== undefined && value !== null) {
          setter(value);
        }
      });
    },
    [applyCachedFacilitySection, facilityID]
  );

  const updateCardDataState = useCallback(
    (updater) => {
      if (!facilityID) return;
      setCardData((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (next) {
          updateFacilityCache(facilityID, { cardData: next });
        }
        return next;
      });
    },
    [facilityID, updateFacilityCache]
  );

  const initialFacilityIdRef = useRef(facilityID);
  const [farms, setFarms] = useState([]);
  const [allFacilities, setAllFacilities] = useState([]);
  const [allCycles, setAllCycles] = useState([]);
  const [selectedFarmId, setSelectedFarmId] = useState("");
  const [localInsightsMessage, setLocalInsightsMessage] = useState("");
  const [localInsightsAllowed, setLocalInsightsAllowed] = useState(null);
  const [localInsightsFacilityId, setLocalInsightsFacilityId] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  const updateSelectedFarmId = useCallback(
    (value) => {
      setSelectedFarmId(value);
      updateGlobalCache({ selectedFarmId: value });
    },
    [updateGlobalCache]
  );

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOnline = () => {
      setIsOffline(false);
    };
    const handleOffline = () => {
      setIsOffline(true);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const persistSelection = useCallback((farmId, facilityId) => {
    try {
      const existingRaw = localStorage.getItem(STORAGE_KEY);
      const existing = existingRaw ? JSON.parse(existingRaw) : {};
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...existing,
          farmId: farmId ?? null,
          facilityId: facilityId ?? null,
        })
      );
    } catch {}
  }, []);

  const readStoredSelection = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    hydrateGlobalFromCache();
  }, [hydrateGlobalFromCache]);

  useEffect(() => {
    if (!isOffline) return;
    hydrateGlobalFromCache();
    hydrateFacilityFromCache(facilityID, { includeAvgDP: true });
  }, [facilityID, hydrateFacilityFromCache, hydrateGlobalFromCache, isOffline]);

  const filteredFacilities = useMemo(() => {
    if (!selectedFarmId) return allFacilities;
    return allFacilities.filter((facility) => `${facility.farmId}` === `${selectedFarmId}`);
  }, [allFacilities, selectedFarmId]);

  const [lineChartDataMortality, setLineChartDataMortality] = useState({
    labels: [],
    datasets: [],
  });

  const [barChartDataWeight, setBarChartDataWeight] = useState({
    labels: [],
    datasets: [],
  });

  const [envChartData, setEnvChartData] = useState({
    labels: [],
    datasets: [],
  });

  const [feedChartData, setFeedChartData] = useState({
    labels: [],
    datasets: [],
  });

  const [waterChartData, setWaterChartData] = useState({
    labels: [],
    datasets: [],
  });

  const [waterqChartData, setWaterQChartData] = useState({
    labels: [],
    datasets: [],
  });

  const [luxChartData, setLuxChartData] = useState({
    labels: [],
    datasets: [],
  });

  const [ftempChartData, setFtempChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [cardData, setCardData] = useState(() => ({ ...INITIAL_CARD_DATA }));
  const [dashboardSummaryMetrics, setDashboardSummaryMetrics] = useState(() => ({
    ...INITIAL_DASHBOARD_SUMMARY_METRICS,
  }));

  const cycleId = useMemo(() => {
    const cardCycleId = resolveCycleId(cardData);
    if (cardCycleId) return cardCycleId;

    const cycleName = cardData?.cycle != null ? `${cardData.cycle}`.trim() : "";
    const normalizedName = cycleName ? cycleName.toLowerCase() : "";
    if (normalizedName) {
      const match = allCycles.find(
        (cycle) =>
          cycle?.broilerId != null &&
          `${cycle.broilerId}` === `${facilityID}` &&
          `${cycle.name ?? ""}`.toLowerCase() === normalizedName
      );
      if (match?.id != null && `${match.id}`.trim().length) {
        return `${match.id}`.trim();
      }
    }

    const fallback = allCycles.find(
      (cycle) =>
        cycle?.broilerId != null && `${cycle.broilerId}` === `${facilityID}` && cycle?.id != null
    );
    return fallback?.id != null ? `${fallback.id}`.trim() : null;
  }, [
    allCycles,
    cardData?.cycle,
    cardData?.cycleId,
    cardData?.cycleID,
    cardData?.CycleID,
    cardData?.cycle_id,
    cardData?.Cycle_ID,
    facilityID,
  ]);

  const [avgTempDP, setAvgTempDP] = useState(() => ({ ...INITIAL_AVG_TEMP }));
  const [avgHumDP, setAvgHumDP] = useState(() => ({ ...INITIAL_AVG_HUM }));
  const [avgNH3DP, setAvgNH3DP] = useState(() => ({ ...INITIAL_AVG_NH3 }));
  const [avgLuxDP, setAvgLuxDP] = useState(() => ({ ...INITIAL_AVG_LUX }));
  const [avgDarkDP, setAvgDarkDP] = useState(() => ({ ...INITIAL_AVG_DARK }));
  const [avgKPIDP, setAvgKPIDP] = useState(() => ({ ...INITIAL_AVG_KPI }));
  const [avgWaterTempDP, setAvgWaterTempDP] = useState(() => ({ ...INITIAL_AVG_WATER_TEMP }));
  const [avgActDP, setAvgActDP] = useState(() => ({ ...INITIAL_AVG_ACT }));
  const [avgFTempDP, setAvgFTempDP] = useState(() => ({ ...INITIAL_AVG_FTEMP }));
  //const [avgWaterpHDP, setAvgWaterpHDP] = useState(null);

  const resetFacilityData = useCallback(() => {
    setLineChartDataMortality(createEmptyChartState());
    setBarChartDataWeight(createEmptyChartState());
    setEnvChartData(createEmptyChartState());
    setFeedChartData(createEmptyChartState());
    setWaterChartData(createEmptyChartState());
    setWaterQChartData(createEmptyChartState());
    setLuxChartData(createEmptyChartState());
    setFtempChartData(createEmptyChartState());
    setAvgTempDP({ ...INITIAL_AVG_TEMP });
    setAvgHumDP({ ...INITIAL_AVG_HUM });
    setAvgNH3DP({ ...INITIAL_AVG_NH3 });
    setAvgLuxDP({ ...INITIAL_AVG_LUX });
    setAvgDarkDP({ ...INITIAL_AVG_DARK });
    setAvgKPIDP({ ...INITIAL_AVG_KPI });
    setAvgWaterTempDP({ ...INITIAL_AVG_WATER_TEMP });
    setAvgActDP({ ...INITIAL_AVG_ACT });
    setAvgFTempDP({ ...INITIAL_AVG_FTEMP });
    setDashboardSummaryMetrics({ ...INITIAL_DASHBOARD_SUMMARY_METRICS });
    setCardData({ ...INITIAL_CARD_DATA });
  }, []);

  useEffect(() => {
    resetFacilityData();
    hydrateFacilityFromCache(facilityID);
  }, [facilityID, hydrateFacilityFromCache, resetFacilityData]);

  useEffect(() => {
    const session = localStorage.getItem("OmnioLogin");
    if (!session) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    const fetchFarmsAndFacilities = async () => {
      if (isOffline) {
        hydrateGlobalFromCache();
        return;
      }

      try {
        const sessionRaw = localStorage.getItem("OmnioLogin");
        const session = sessionRaw ? JSON.parse(sessionRaw) : null;
        const userID = session?.ID;
        if (!userID) {
          hydrateGlobalFromCache();
          return;
        }

        const [farmsRes, broilersRes] = await Promise.all([
          axios.get(farmsForUserUrl(userID), createSkipAuthConfig()),
          axios.get(allBroilersUrl(), createSkipAuthConfig()),
        ]);

        if (cancelled || !isMountedRef.current) return;

        const farmsData = Array.isArray(farmsRes.data) ? farmsRes.data : [];
        const broilersData = Array.isArray(broilersRes.data) ? broilersRes.data : [];

        const pick = (...vals) =>
          vals.find(
            (val) => val !== undefined && val !== null && `${val}`.toString().trim().length > 0
          );

        const toId = (value) => {
          if (value == null) return null;
          const str = `${value}`.trim();
          return str.length ? str : null;
        };

        const normalizedFarms = farmsData
          .map((farm) => {
            const id = toId(pick(farm?.farmId, farm?.FarmID, farm?.farmID, farm?.id, farm?.ID));
            const nameSource = pick(farm?.name, farm?.farmName, farm?.FarmName);
            const name = nameSource == null ? null : `${nameSource}`;
            return { id, name };
          })
          .filter((farm) => farm.id && farm.name);

        const normalizedFacilities = broilersData
          .map((broiler) => {
            const id = toId(
              pick(
                broiler?.broilerID,
                broiler?.broilerId,
                broiler?.BroilerID,
                broiler?.facilityID,
                broiler?.facilityId,
                broiler?.id,
                broiler?.ID
              )
            );
            const nameSource = pick(
              broiler?.name,
              broiler?.broilerName,
              broiler?.facilityName,
              broiler?.BroilerName
            );
            const name = nameSource == null ? null : `${nameSource}`;
            const farmId = toId(pick(broiler?.farmID, broiler?.farmId));
            return { id, name, farmId };
          })
          .filter((broiler) => broiler.id && broiler.name && broiler.farmId);

        let cyclesRaw = [];
        if (Array.isArray(farmsRes.data?.tbl_Cycles)) {
          cyclesRaw = farmsRes.data.tbl_Cycles;
        } else if (Array.isArray(farmsRes.data)) {
          const withTbl = farmsRes.data.find((row) => Array.isArray(row?.tbl_Cycles));
          if (withTbl) cyclesRaw = withTbl.tbl_Cycles;
        }

        const normalizedCycles = (Array.isArray(cyclesRaw) ? cyclesRaw : [])
          .map((cycle) => {
            const id = toId(pick(cycle?.id, cycle?.ID, cycle?.cycleId, cycle?.cycleID));
            const nameSource = pick(
              cycle?.name,
              cycle?.cycleName,
              cycle?.CycleName,
              cycle?.cycle,
              cycle?.Cycle
            );
            const name = nameSource == null ? null : `${nameSource}`;
            const broilerId = toId(
              pick(
                cycle?.broilerid,
                cycle?.broilerId,
                cycle?.BroilerID,
                cycle?.facilityID,
                cycle?.facilityId
              )
            );
            return { id, name, broilerId };
          })
          .filter((cycle) => cycle.id && cycle.broilerId);

        const farmsReady = normalizedFarms.filter((farm) =>
          normalizedFacilities.some((facility) => facility.farmId === farm.id)
        );

        if (cancelled || !isMountedRef.current) return;

        setFarms(farmsReady);
        setAllFacilities(normalizedFacilities);
        setAllCycles(normalizedCycles);
        updateGlobalCache({
          farms: farmsReady,
          allFacilities: normalizedFacilities,
        });

        const stored = readStoredSelection();
        const facilityFromUrl = initialFacilityIdRef.current
          ? `${initialFacilityIdRef.current}`
          : null;
        const storedFacilityId = stored?.facilityId != null ? `${stored.facilityId}`.trim() : null;
        const storedFarmId = stored?.farmId != null ? `${stored.farmId}`.trim() : null;

        const findFacilityById = (id) =>
          id == null ? null : normalizedFacilities.find((facility) => facility.id === id);

        let targetFacility =
          findFacilityById(facilityFromUrl) ||
          findFacilityById(storedFacilityId) ||
          (storedFarmId
            ? normalizedFacilities.find((facility) => facility.farmId === storedFarmId) || null
            : null) ||
          normalizedFacilities[0] ||
          null;

        if (cancelled || !isMountedRef.current) return;

        if (targetFacility) {
          updateSelectedFarmId(targetFacility.farmId ?? "");
          persistSelection(targetFacility.farmId ?? null, targetFacility.id ?? null);
          if (!facilityFromUrl || facilityFromUrl !== targetFacility.id) {
            navigate(`/dashboard?facility=${targetFacility.id}`, { replace: !facilityFromUrl });
          }
        } else if (farmsReady[0]) {
          const farmId = farmsReady[0].id ?? "";
          updateSelectedFarmId(farmId);
          persistSelection(farmId ?? null, null);
        }
      } catch (error) {
        console.error("Failed to load farms and facilities for dashboard", error);
        hydrateGlobalFromCache();
      }
    };

    fetchFarmsAndFacilities();

    return () => {
      cancelled = true;
    };
  }, [
    hydrateGlobalFromCache,
    isOffline,
    navigate,
    persistSelection,
    refreshTick,
    readStoredSelection,
    updateGlobalCache,
    updateSelectedFarmId,
  ]);

  useEffect(() => {
    if (!facilityID || !allFacilities.length) return;
    const match = allFacilities.find((facility) => facility.id === facilityID);
    if (!match) return;
    if ((match.farmId ?? "") !== selectedFarmId) {
      updateSelectedFarmId(match.farmId ?? "");
    }
    persistSelection(match.farmId ?? null, match.id ?? null);
  }, [facilityID, allFacilities, persistSelection, selectedFarmId, updateSelectedFarmId]);

  const checkLocalInsightsSubscription = useCallback(async (farmId, facilityId) => {
    if (!farmId || !facilityId) {
      setLocalInsightsAllowed(null);
      setLocalInsightsFacilityId("");
      return false;
    }

    if (IS_DEVELOPMENT) {
      setLocalInsightsFacilityId(`${facilityId}`);
      setLocalInsightsAllowed(true);
      return true;
    }

    try {
      const response = await axios.get(broilersByFarmUrl(farmId));
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const selectedFacility = rows.find((row) => `${row?.id}` === `${facilityId}`);
      const localInsightsValue = selectedFacility?.localInsights;
      const isSubscribed =
        localInsightsValue === true || `${localInsightsValue}`.toLowerCase() === "true";

      setLocalInsightsFacilityId(`${facilityId}`);
      setLocalInsightsAllowed(Boolean(selectedFacility && isSubscribed));
      if (selectedFacility && isSubscribed) {
        setRefreshTick((value) => value + 1);
      }
      if (selectedFacility && !isSubscribed) {
        setLocalInsightsMessage(
          `Please subscribe ${selectedFacility.name} to Local Insights to view Dashboard`
        );
      }
      return Boolean(selectedFacility && isSubscribed);
    } catch (error) {
      console.error("Failed to check Local Insights subscription", error);
      setLocalInsightsAllowed(null);
      setLocalInsightsFacilityId("");
      return false;
    }
  }, []);

  useEffect(() => {
    if (!facilityID || !selectedFarmId) return;
    checkLocalInsightsSubscription(selectedFarmId, facilityID);
  }, [checkLocalInsightsSubscription, facilityID, selectedFarmId]);

  const canLoadDashboardData =
    (IS_DEVELOPMENT && Boolean(facilityID)) ||
    (localInsightsAllowed === true && `${localInsightsFacilityId}` === `${facilityID}`);

  const handleFarmChange = useCallback(
    (event) => {
      const newFarmId = event.target.value;
      updateSelectedFarmId(newFarmId);
      const facilitiesForFarm = allFacilities.filter((facility) => facility.farmId === newFarmId);
      if (!facilitiesForFarm.length) {
        persistSelection(newFarmId ?? null, null);
        return;
      }
      const existingSelection = facilitiesForFarm.find((facility) => facility.id === facilityID);
      const nextFacility = existingSelection || facilitiesForFarm[0];
      persistSelection(newFarmId ?? null, nextFacility.id ?? null);
      if (facilityID !== nextFacility.id) {
        navigate(`/dashboard?facility=${nextFacility.id}`);
      }
    },
    [allFacilities, facilityID, navigate, persistSelection, updateSelectedFarmId]
  );

  const handleFacilityChange = useCallback(
    (event) => {
      const newFacilityId = event.target.value;
      if (!newFacilityId) return;
      persistSelection(selectedFarmId ?? null, newFacilityId);
      checkLocalInsightsSubscription(selectedFarmId, newFacilityId);
      if (facilityID !== newFacilityId) {
        navigate(`/dashboard?facility=${newFacilityId}`);
      }
    },
    [checkLocalInsightsSubscription, facilityID, navigate, persistSelection, selectedFarmId]
  );

  const handleDashboardRefresh = useCallback(() => {
    if (!facilityID || isOffline || !canLoadDashboardData) return;
    setRefreshTick((value) => value + 1);
  }, [canLoadDashboardData, facilityID, isOffline]);

  const handleMortalityRedirect = useCallback(() => {
    if (!facilityID) return;
    saveMortalityRedirect({
      facilityId: facilityID,
      timestamp: Date.now(),
    });
    navigate(`/reports/facilities?facility=${encodeURIComponent(facilityID)}`, {
      state: { facilityId: facilityID },
    });
  }, [facilityID, navigate]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("lineChartDataMortality", setLineChartDataMortality);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchMortalityChartData = async () => {
      try {
        const response = await axios.post(mortalityUrl(facilityID), null, createSkipAuthConfig());
        const payload = response.data || {};
        if (cancelled || !isMountedRef.current) return;

        const chartData = buildMortalityChartData(payload, {
          limit: 7,
          datasetType: "line",
          datasetOverrides: {
            borderColor: "#F2F2F2",
            borderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 10,
            pointBorderWidth: 0,
            pointHoverBorderWidth: 0,
            fill: false,
            tension: 0.4,
            datalabels: {
              anchor: "end",
              align: "start",
              rotation: 0,
            },
          },
        });

        setLineChartDataMortality(chartData);
        updateFacilityCache(facilityID, { lineChartDataMortality: chartData });
      } catch (error) {
        console.error("Error fetching mortality chart data", error);
        applyCached();
      }
    };
    fetchMortalityChartData();

    return () => {
      cancelled = true;
    };
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("barChartDataWeight", setBarChartDataWeight);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchWeightChartData = async () => {
      try {
        const response = await axios.post(weightUrl(facilityID), null, createSkipAuthConfig());

        const apiData = response.data[0];

        const sanitizeString = (value) => {
          if (value === null || value === undefined) return null;
          const asString = String(value).trim();
          if (!asString || asString.toLowerCase() === "null") return null;
          return asString;
        };

        const sanitizeNumber = (value) => {
          if (value === null || value === undefined) return null;
          if (value === "" || value === "null") return null;
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : null;
        };

        const rawStatusArray = Array.isArray(apiData.status) ? apiData.status : [];
        const statusArray = rawStatusArray.map((status) => {
          const sanitized = sanitizeString(status);
          return sanitized ? sanitized.toLowerCase() : null;
        });

        const STATUS_COLORS = {
          green: "#4caf50",
          amber: "#ffb300",
          red: "#f44336",
          orange: "#ff9800",
          yellow: "#ffeb3b",
          neutral: "#9e9e9e",
        };

        const colorForStatus = (status) => STATUS_COLORS[status] || STATUS_COLORS.neutral;

        const rawPlacementDay = apiData.placement?.day?.[0];
        const rawPlacementWeight = apiData.placement?.placement_weight?.[0];
        const placementDay = sanitizeString(rawPlacementDay);
        const placementWeight = sanitizeNumber(rawPlacementWeight);

        const placementEntry =
          placementDay != null
            ? {
                type: "placement",
                day: placementDay,
                weight: placementWeight,
                target: null,
                deviation: null,
                status: statusArray[0] ?? null,
              }
            : null;

        const weeklyDaysRaw = apiData.weekly_weight?.day ?? [];
        const weeklyWeightsRaw = apiData.weekly_weight?.weekly_weight ?? [];
        const weeklyTargetsRaw = apiData.weekly_weight?.weight_target ?? [];
        const weeklyDeviationRaw = apiData.weekly_weight?.deviationp ?? [];
        const cvRaw = Array.isArray(apiData.cv) ? apiData.cv : [];
        const cvStatusRaw = Array.isArray(apiData.cv_status) ? apiData.cv_status : [];
        const cvTarget = sanitizeNumber(apiData.cv_target);

        const statusOffset = placementEntry ? 1 : 0;

        const weeklyEntries = weeklyDaysRaw.reduce((acc, dayValue, index) => {
          const day = sanitizeString(dayValue);
          if (!day) return acc;

          acc.push({
            type: "weekly",
            day,
            weight: sanitizeNumber(weeklyWeightsRaw[index]),
            target: sanitizeNumber(weeklyTargetsRaw[index]),
            deviation: sanitizeNumber(weeklyDeviationRaw[index]),
            status: statusArray[index + statusOffset] ?? null,
            cv: sanitizeNumber(cvRaw[index + statusOffset]),
            cvStatus: sanitizeString(cvStatusRaw[index + statusOffset]),
          });

          return acc;
        }, []);

        const combinedEntries = [];
        if (placementEntry) {
          combinedEntries.push(placementEntry);
        }
        combinedEntries.push(...weeklyEntries);

        const labels = combinedEntries.map((entry) => entry.day);
        const placementData = combinedEntries.map((entry) =>
          entry.type === "placement" ? entry.weight : null
        );
        const placementColors = combinedEntries.map((entry) =>
          entry.type === "placement" ? colorForStatus(entry.status) : "transparent"
        );
        const weeklyData = combinedEntries.map((entry) =>
          entry.type === "weekly" ? entry.weight : null
        );
        const weeklyColors = combinedEntries.map((entry) =>
          entry.type === "weekly" ? colorForStatus(entry.status) : "transparent"
        );
        const weeklyTargetData = combinedEntries.map((entry) =>
          entry.type === "weekly" ? entry.target : null
        );
        const weeklyMetadata = combinedEntries.map((entry) => ({
          day: entry.type === "weekly" ? entry.day : null,
          weekly_weight: entry.type === "weekly" ? entry.weight : null,
          deviationp: entry.type === "weekly" ? entry.deviation : null,
          weight_target: entry.type === "weekly" ? entry.target : null,
          cv: entry.type === "weekly" ? entry.cv : null,
          cv_status: entry.type === "weekly" ? entry.cvStatus : null,
          cv_target: entry.type === "weekly" ? cvTarget : null,
        }));

        if (cancelled || !isMountedRef.current) return;

        const chartData = {
          labels,
          datasets: [
            {
              label: "Placement",
              data: placementData,
              backgroundColor: placementColors,
              barThickness: 15,
            },
            {
              label: "Weekly Weight",
              data: weeklyData,
              backgroundColor: weeklyColors,
              metadata: weeklyMetadata,
              tooltipDisplayColors: false,
              barPercentage: 0.6,
              categoryPercentage: 0.8,
            },
            {
              label: "Weight Target",
              data: weeklyTargetData,
              backgroundColor: "#e0e0e0",
              tooltipDisplay: false,
              barPercentage: 0.2,
              categoryPercentage: 0.8,
            },
          ],
        };

        console.log("Weight Chart Data:", chartData);
        setBarChartDataWeight(chartData);
        updateFacilityCache(facilityID, { barChartDataWeight: chartData });
      } catch (error) {
        console.error("Error fetching weight chart data", error);
        applyCached();
      }
    };

    fetchWeightChartData();

    return () => {
      cancelled = true;
    };
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    const applyCached = () => {
      applyFacilityCached("envChartData", setEnvChartData);
    };

    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchEnvChartData = async () => {
      try {
        const response = await axios.post(envUrl(facilityID), null, createSkipAuthConfig());

        const [tempData, humData, nh3Data] = response.data;

        const labels = (tempData.timestamp_name || []).map((ts) => (ts ? ts.split("T")[1] : ""));

        const statusColors = {
          Green: "#4caf50",
          Red: "#f44336",
          Orange: "#ff9800",
          Yellow: "#ffeb3b",
          Neutral: "#9e9e9e",
        };

        const tempValues = tempData.temp.map(Number);
        const tempDeviation = tempData.deviationp.map(Number);
        const tempStatus = tempData.status;

        const humValues = humData.hum.map(Number);
        const humDeviation = humData.deviationp.map(Number);
        const humStatus = humData.status;

        const nh3Values = nh3Data.nh3.map(Number);
        const nh3Deviation = nh3Data.deviationp.map(Number);
        const nh3Status = nh3Data.status;

        const tempColors = tempStatus.map((s) => statusColors[s] || statusColors.Neutral);
        const humColors = humStatus.map((s) => statusColors[s] || statusColors.Neutral);
        const nh3Colors = nh3Status.map((s) => statusColors[s] || statusColors.Neutral);

        const tempMetadata = tempValues.map((v, i) => ({
          temp: v,
          deviationp: tempDeviation[i],
          target: tempData.target,
        }));

        const humMetadata = humValues.map((v, i) => ({
          hum: v,
          deviationp: humDeviation[i],
          target: humData.target,
        }));

        const nh3Metadata = nh3Values.map((v, i) => ({
          nh3: v,
          deviationp: nh3Deviation[i],
          target: nh3Data.target,
        }));

        if (cancelled || !isMountedRef.current) return;

        const chartData = {
          labels,
          datasets: [
            {
              label: "Temp",
              data: tempValues,
              metadata: tempMetadata,
              borderColor: "#D9D9D9",
              pointStyle: "circle",
              backgroundColor: tempColors,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 1,
              fill: false,
              datalabels: { display: false },
            },
            {
              label: "Hum",
              data: humValues,
              metadata: humMetadata,
              borderColor: "#4D93D9",
              backgroundColor: humColors,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 1,
              fill: false,
              datalabels: { display: false },
            },
            {
              label: "NH3",
              data: nh3Values,
              metadata: nh3Metadata,
              borderColor: "#BE5014",
              backgroundColor: nh3Colors,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 1,
              fill: false,
              datalabels: { display: false },
            },
          ],
        };

        setEnvChartData(chartData);
        updateFacilityCache(facilityID, { envChartData: chartData });
      } catch (err) {
        console.error("Error fetching sensor chart data:", err);
        applyCached();
      }
    };

    fetchEnvChartData();
    const interval = startDashboardPolling(fetchEnvChartData);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    };
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    const applyCached = () => {
      applyFacilityCached("luxChartData", setLuxChartData);
    };

    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchLuxChartData = async () => {
      try {
        const response = await axios.post(luxUrl(facilityID), null, createSkipAuthConfig());

        const [luxData] = response.data;

        const labels = (luxData.timestamp_name || []).map((ts) => (ts ? ts.split("T")[1] : ""));

        const statusColors = {
          Green: "#4caf50",
          Red: "#f44336",
          Orange: "#ff9800",
          Yellow: "#ffeb3b",
          Neutral: "#9e9e9e",
        };

        const luxValues = luxData.lux.map(Number);
        const luxDeviation = luxData.deviationp.map(Number);
        const luxStatus = luxData.status;

        const luxColors = luxStatus.map((s) => statusColors[s] || statusColors.Neutral);

        const luxMetadata = luxValues.map((v, i) => ({
          lux: v,
          deviationp: luxDeviation[i],
          target: luxData.target,
        }));

        if (cancelled || !isMountedRef.current) return;

        const chartData = {
          labels,
          datasets: [
            {
              label: "Lighting",
              data: luxValues,
              metadata: luxMetadata,
              borderColor: "#DCEAF7",
              pointStyle: "circle",
              backgroundColor: luxColors,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 1,
              fill: false,
              datalabels: { display: false },
            },
          ],
        };

        setLuxChartData(chartData);
        updateFacilityCache(facilityID, { luxChartData: chartData });
      } catch (err) {
        console.error("Error fetching lux chart data:", err);
        applyCached();
      }
    };

    fetchLuxChartData();
    const interval = startDashboardPolling(fetchLuxChartData);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    };
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    const applyCached = () => {
      applyFacilityCached("ftempChartData", setFtempChartData);
    };

    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchFtempChartData = async () => {
      try {
        const response = await axios.post(floorTempUrl(facilityID), null, createSkipAuthConfig());

        const [ftempData] = response.data;

        const labels = (ftempData.timestamp_name || []).map((ts) => (ts ? ts.split("T")[1] : ""));

        const statusColors = {
          Green: "#4caf50",
          Red: "#f44336",
          Orange: "#ff9800",
          Yellow: "#ffeb3b",
          Neutral: "#9e9e9e",
        };

        const ftempValues = ftempData.ftemp.map(Number);
        const ftempDeviation = ftempData.deviationp.map(Number);
        const ftempStatus = ftempData.status;

        const ftempColors = ftempStatus.map((s) => statusColors[s] || statusColors.Neutral);

        const ftempMetadata = ftempValues.map((v, i) => ({
          ftemp: v,
          deviationp: ftempDeviation[i],
          target: ftempData.target,
        }));

        if (cancelled || !isMountedRef.current) return;

        const chartData = {
          labels,
          datasets: [
            {
              label: "Floor Temp",
              data: ftempValues,
              metadata: ftempMetadata,
              borderColor: "#DCEAF7",
              pointStyle: "circle",
              backgroundColor: ftempColors,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 1,
              fill: false,
            },
          ],
        };

        setFtempChartData(chartData);
        updateFacilityCache(facilityID, { ftempChartData: chartData });
      } catch (err) {
        console.error("Error fetching lux chart data:", err);
        applyCached();
      }
    };

    fetchFtempChartData();
    const interval = startDashboardPolling(fetchFtempChartData);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    };
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    const applyCached = () => {
      applyFacilityCached("cardData", setCardData);
    };

    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchCardData = async () => {
      try {
        const response = await axios.post(cardSummaryUrl(facilityID), null, createSkipAuthConfig());
        if (cancelled || !isMountedRef.current) return;
        const nextCard = response.data[0];
        if (nextCard) {
          updateCardDataState((prev) => ({ ...prev, ...nextCard }));
        }
      } catch (err) {
        console.error("Error fetching card data:", err);
        applyCached();
      }
    };

    fetchCardData();
    const interval = startDashboardPolling(fetchCardData);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    };
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateCardDataState]);

  useEffect(() => {
    const applyCached = () => {
      applyFacilityCached("dashboardSummaryMetrics", setDashboardSummaryMetrics);
    };

    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
      return;
    }

    if (!canLoadDashboardData) return;

    if (isOffline || !cycleId) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchDashboardSummaryMetrics = async () => {
      try {
        const response = await axios.post(
          dashboardSummaryMetricsUrl(facilityID, cycleId),
          null,
          createSkipAuthConfig()
        );
        const payload = unwrapResponseRecord(response.data);
        const nextMetrics = {
          rateMortality: pickNullableNumber(payload, [
            "rateMortality",
            "mortalityRate",
            "mortality_rate",
            "rate_mortality",
          ]),
          mortality: pickNullableNumber(payload, ["mortality", "mortalities"]),
          culls: pickNullableNumber(payload, ["culls", "cull"]),
          liveBirds: pickNullableNumber(payload, ["liveBirds", "live_birds", "liveCount"]),
          avgWeight: pickNullableNumber(payload, [
            "avgWeight",
            "averageWeight",
            "avg_weight",
            "weight_average",
          ]),
          totalWeight: pickNullableNumber(payload, ["totalWeight", "weightTotal", "total_weight"]),
          fcr: pickNullableNumber(payload, ["fcr", "FCR", "avgFcr", "avg_fcr"]),
          totalFeed: pickNullableNumber(payload, ["totalFeed", "feedTotal", "total_feed"]),
          avgWater: pickNullableNumber(payload, [
            "avgWater",
            "averageWater",
            "avg_water",
            "water_avgbird",
          ]),
          totalWater: pickNullableNumber(payload, ["totalWater", "waterTotal", "total_water"]),
        };

        if (cancelled || !isMountedRef.current) return;
        setDashboardSummaryMetrics(nextMetrics);
        updateFacilityCache(facilityID, { dashboardSummaryMetrics: nextMetrics });
      } catch (error) {
        console.error("Error fetching dashboard summary metrics", error);
        applyCached();
      }
    };

    fetchDashboardSummaryMetrics();
    const interval = startDashboardPolling(fetchDashboardSummaryMetrics);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    };
  }, [
    applyFacilityCached,
    canLoadDashboardData,
    cycleId,
    facilityID,
    isOffline,
    navigate,
    refreshTick,
    updateFacilityCache,
  ]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("feedChartData", setFeedChartData);
      applyFacilityCached("cardData", setCardData);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchFeedChartData = async () => {
      try {
        const response = await axios.post(feedUrl(facilityID), null, createSkipAuthConfig());

        const rawPayload = response.data ?? {};
        const apiEntry = Array.isArray(rawPayload) ? rawPayload[0] ?? {} : rawPayload;

        const feedSection = apiEntry.feed ?? {};
        const fcrSection = apiEntry.weekly_FCR ?? apiEntry.fcr ?? {};
        const statusSection = apiEntry.status ?? apiEntry.fcr_status ?? fcrSection.status ?? [];

        const sanitizeNumber = (value) => {
          if (value === null || value === undefined || value === "" || value === "null")
            return null;
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : null;
        };

        const sanitizeString = (value) => {
          if (value === null || value === undefined || value === "null") return null;
          return String(value);
        };

        const timestampsRaw = feedSection.timestamp ?? apiEntry.timestamp ?? [];
        const timestamps = Array.isArray(timestampsRaw)
          ? timestampsRaw.map((ts) => {
              if (!ts || ts === "null") return null;
              const str = String(ts);
              return str.includes("T") ? str.split("T")[0] : str;
            })
          : [];

        const feedValuesRaw = feedSection.feed ?? apiEntry.feed ?? [];
        const feedValues = Array.isArray(feedValuesRaw) ? feedValuesRaw.map(sanitizeNumber) : [];

        const fcrValuesSource =
          fcrSection.weekly_fcr ??
          fcrSection.fcr ??
          apiEntry.weekly_fcr ??
          apiEntry.fcr ??
          fcrSection.fcr_weekly ??
          apiEntry.fcr_weekly;
        const fcrValues = Array.isArray(fcrValuesSource) ? fcrValuesSource.map(sanitizeNumber) : [];

        const fcrTargetSource = fcrSection.fcr_target ?? apiEntry.fcr_target;
        const fcrTarget = Array.isArray(fcrTargetSource) ? fcrTargetSource.map(sanitizeNumber) : [];

        const fcrDeviationRaw =
          fcrSection.deviationp ??
          apiEntry.deviationp ??
          fcrSection.fcr_deviationp ??
          apiEntry.fcr_deviationp ??
          [];
        const fcrDeviation = Array.isArray(fcrDeviationRaw)
          ? fcrDeviationRaw.map(sanitizeNumber)
          : [];

        const fcrDaysRaw =
          fcrSection.day ?? apiEntry.day ?? fcrSection.fcr_day ?? apiEntry.fcr_day ?? [];
        const fcrDays = Array.isArray(fcrDaysRaw) ? fcrDaysRaw.map(sanitizeString) : [];

        const fcrStatusesRaw = Array.isArray(statusSection)
          ? statusSection.map((status) => {
              const sanitized = sanitizeString(status);
              return sanitized ? sanitized.toLowerCase() : null;
            })
          : [];
        const fcrMessage = sanitizeString(fcrSection.fcr_message ?? apiEntry.fcr_message);

        const STATUS_COLORS = {
          green: "#4caf50",
          amber: "#ffb300",
          red: "#f44336",
          yellow: "#fdd835",
          orange: "#fb8c00",
        };

        const feedChartPoints = feedValues
          .map((feedValue, index) => ({
            day: fcrDays[index] ?? null,
            feedValue,
            sourceIndex: index,
            timestamp: timestamps[index] ?? null,
          }))
          .filter((point) => point.day !== null);

        const chartFeedValues = feedChartPoints.map((point) => point.feedValue);
        const dataLength = feedChartPoints.length;
        const labels = [];
        const metadata = [];
        const backgroundColor = [];

        for (let index = 0; index < dataLength; index += 1) {
          const { day, feedValue, sourceIndex, timestamp } = feedChartPoints[index];
          const labelCandidate = day || `Entry ${index + 1}`;
          labels.push(labelCandidate ?? `Entry ${index + 1}`);

          const statusKey = fcrStatusesRaw[sourceIndex];
          const color = (statusKey && STATUS_COLORS[statusKey]) || "#c3971d";

          backgroundColor.push(color);
          metadata.push({
            feed: feedValue,
            fcr: sourceIndex < fcrValues.length ? fcrValues[sourceIndex] : null,
            target: sourceIndex < fcrTarget.length ? fcrTarget[sourceIndex] : null,
            deviationp: sourceIndex < fcrDeviation.length ? fcrDeviation[sourceIndex] : null,
            day,
            status: statusKey ?? null,
            timestamp,
          });
        }

        const chartData = {
          labels,
          datasets: [
            {
              label: "Feed Consumption (kg)",
              data: chartFeedValues,
              metadata,
              backgroundColor: backgroundColor.length ? backgroundColor : "#c3971d",
            },
          ],
        };

        if (cancelled || !isMountedRef.current) return;
        setFeedChartData(chartData);
        updateFacilityCache(facilityID, { feedChartData: chartData });

        const totalFeedRaw = feedSection.feed_total ?? apiEntry.feed_total ?? null;
        const totalFeed = sanitizeNumber(totalFeedRaw);

        const findLastIndexWith = (values, predicate) => {
          if (!Array.isArray(values)) return -1;
          for (let idx = values.length - 1; idx >= 0; idx -= 1) {
            if (predicate(values[idx], idx)) return idx;
          }
          return -1;
        };

        const latestFcrIndex = findLastIndexWith(fcrValues, (val) => val !== null);
        const latestTargetIndex = findLastIndexWith(fcrTarget, (val) => val !== null);
        const latestDeviationIndex = findLastIndexWith(fcrDeviation, (val) => val !== null);
        const latestStatusIndex = findLastIndexWith(fcrStatusesRaw, (val) => val);
        const latestDayIndex = findLastIndexWith(fcrDays, (val) => val);

        let activeIndex = latestFcrIndex;
        if (activeIndex < 0) activeIndex = latestTargetIndex;
        if (activeIndex < 0) activeIndex = latestDeviationIndex;
        if (activeIndex < 0) activeIndex = latestStatusIndex;
        if (activeIndex < 0) activeIndex = latestDayIndex;

        updateCardDataState((prev) => ({
          ...prev,
          feed_total: totalFeed ?? prev?.feed_total ?? null,
          feed: feedValues,
          fcr:
            latestFcrIndex >= 0
              ? fcrValues[latestFcrIndex]
              : Array.isArray(fcrValuesSource)
              ? null
              : prev?.fcr ?? null,
          fcr_day:
            activeIndex >= 0 && activeIndex < fcrDays.length
              ? fcrDays[activeIndex]
              : prev?.fcr_day ?? null,
          fcr_target:
            activeIndex >= 0 && activeIndex < fcrTarget.length
              ? fcrTarget[activeIndex]
              : Array.isArray(fcrTargetSource)
              ? null
              : prev?.fcr_target ?? null,
          fcr_message: fcrMessage ?? prev?.fcr_message ?? null,
          fcr_deviationp:
            activeIndex >= 0 && activeIndex < fcrDeviation.length
              ? fcrDeviation[activeIndex]
              : prev?.fcr_deviationp ?? null,
          fcr_status:
            activeIndex >= 0 && activeIndex < fcrStatusesRaw.length
              ? fcrStatusesRaw[activeIndex]
              : prev?.fcr_status ?? null,
        }));
      } catch (error) {
        console.error("Error fetching feed chart data", error);
        applyCached();
      }
    };

    fetchFeedChartData();
    const interval = startDashboardPolling(fetchFeedChartData);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [
    applyFacilityCached,
    facilityID,
    isOffline,
    navigate,
    refreshTick,
    updateCardDataState,
    updateFacilityCache,
  ]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("waterChartData", setWaterChartData);
      applyFacilityCached("cardData", setCardData);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    if (!cycleId) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchWaterChartData = async () => {
      try {
        const response = await axios.post(
          waterUrl(facilityID, cycleId),
          null,
          createSkipAuthConfig()
        );
        const apiData = response.data || {};

        let labels = [];
        let values = [];
        let avgBirdSeries = [];
        let waterTotal = apiData.water_total;
        let waterAvgHour = apiData.water_avghour;
        let waterDailyBird = apiData.water_dailybird;

        if (Array.isArray(apiData.timestamp) && Array.isArray(apiData.water_usage)) {
          labels = apiData.timestamp.slice();
          values = apiData.water_usage.slice();
          avgBirdSeries = Array.isArray(apiData.water_avgbird) ? apiData.water_avgbird.slice() : [];
        } else if (Array.isArray(apiData)) {
          const combined = apiData
            .map((row) => ({
              timestamp: row?.timestamp,
              water_usage: row?.water_usage,
              water_avgbird: row?.water_avgbird,
              water_total: row?.water_total,
              water_dailybird: row?.water_dailybird,
              water_avghour: row?.water_avghour,
            }))
            .filter((row) => row.timestamp != null);

          combined.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          const last7Days = combined.slice(-7);
          labels = last7Days.map((item) => item.timestamp);
          values = last7Days.map((item) => item.water_usage);
          avgBirdSeries = last7Days.map((item) => item.water_avgbird);
          const lastRow = last7Days[last7Days.length - 1];
          waterTotal = lastRow?.water_total ?? waterTotal;
          waterDailyBird = lastRow?.water_dailybird ?? waterDailyBird;
          waterAvgHour = lastRow?.water_avghour ?? waterAvgHour;
        }

        const chartData = {
          labels,
          datasets: [
            {
              data: values,
              metadata: avgBirdSeries.map((value) => ({
                water_avgbird: value,
                water_bird: value,
              })),
              label: "Water Consumption",
              showLegend: false,
              spanGaps: false,
              backgroundColor: "#153E64",
              borderColor: "#D8D8D8",
              tension: 0.4,
              pointRadius: 6,
              pointHoverRadius: 8,
              borderWidth: 1,
              fill: false,
              datalabels: { display: true },
            },
          ],
        };

        console.log("Water Chart Data:", chartData);
        if (cancelled || !isMountedRef.current) return;
        setWaterChartData(chartData);
        updateFacilityCache(facilityID, { waterChartData: chartData });
        updateCardDataState((prev) => ({
          ...prev,
          water_usage: apiData.water_usage ?? prev?.water_usage,
          water_avgbird: apiData.water_avgbird ?? prev?.water_avgbird,
          water_total: waterTotal ?? prev?.water_total,
          water_dailybird: waterDailyBird ?? prev?.water_dailybird,
          water_avghour: waterAvgHour ?? prev?.water_avghour,
        }));
      } catch (error) {
        console.error("Error fetching water chart data", error);
        applyCached();
      }
    };

    fetchWaterChartData();
    const interval = startDashboardPolling(fetchWaterChartData);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [
    applyFacilityCached,
    cycleId,
    facilityID,
    isOffline,
    navigate,
    refreshTick,
    updateCardDataState,
    updateFacilityCache,
  ]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("waterqChartData", setWaterQChartData);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchWaterQChartData = async () => {
      try {
        const response = await axios.post(
          waterQualityUrl(facilityID),
          null,
          createSkipAuthConfig()
        );

        const [tempData, phData] = response.data;

        const labels = (tempData.timestamp_name || []).map((ts) => (ts ? ts.split("T")[1] : ""));

        const statusColors = {
          Green: "#4caf50",
          Red: "#f44336",
          Orange: "#ff9800",
          Yellow: "#ffeb3b",
          Neutral: "#9e9e9e",
        };

        const tempValues = tempData.temp.map(Number);
        const tempDeviation = tempData.deviationp.map(Number);
        const tempStatus = tempData.status;

        const phValues = phData.ph.map(Number);
        const phDeviation = phData.deviationp.map(Number);
        const phStatus = phData.status;

        const tempColors = tempStatus.map((s) => statusColors[s] || statusColors.Neutral);
        const phColors = phStatus.map((s) => statusColors[s] || statusColors.Neutral);

        const tempMetadata = tempValues.map((v, i) => ({
          temp: v,
          deviationp: tempDeviation[i],
        }));

        const phMetadata = phValues.map((v, i) => ({
          ph: v,
          deviationp: phDeviation[i],
        }));

        if (cancelled || !isMountedRef.current) return;

        const chartData = {
          labels,
          datasets: [
            {
              label: "Water Temp",
              data: tempValues,
              metadata: tempMetadata,
              borderColor: "#DCEAF7",
              pointStyle: "circle",
              backgroundColor: tempColors,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 1,
              fill: false,
              datalabels: { display: false },
            },
            {
              label: "pH",
              data: phValues,
              metadata: phMetadata,
              borderColor: "#104861",
              backgroundColor: phColors,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 1,
              fill: false,
              datalabels: { display: false },
            },
          ],
        };

        setWaterQChartData(chartData);
        updateFacilityCache(facilityID, { waterqChartData: chartData });
      } catch (err) {
        console.error("Error fetching sensor chart data:", err);
        applyCached();
      }
    };

    fetchWaterQChartData();
    const interval = startDashboardPolling(fetchWaterQChartData);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("avgDarkDP", setAvgDarkDP);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchAvgDarkDP = async () => {
      try {
        const response = await axios.post(avgDarkUrl(facilityID), null, createSkipAuthConfig());
        const data = response.data;

        if (cancelled || !isMountedRef.current) return;
        const next = normalizeAverageDeviationPayload(data, "avg_dark_dp", "avg_darkdp_error");
        setAvgDarkDP(next);
        updateFacilityCache(facilityID, { avgDarkDP: next });
      } catch (err) {
        console.error("Error fetching Avg Dark Period data:", err);
        if (cancelled || !isMountedRef.current) return;
        setAvgDarkDP((prev) => ({ ...prev, avg_dark_dp: null, avg_darkdp_error: true }));
      }
    };

    fetchAvgDarkDP();
    const interval = startDashboardPolling(fetchAvgDarkDP);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("avgTempDP", setAvgTempDP);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchAvgTempDP = async () => {
      try {
        const response = await axios.post(avgTempUrl(facilityID), null, createSkipAuthConfig());
        const data = response.data;

        if (cancelled || !isMountedRef.current) return;
        const next = normalizeAverageDeviationPayload(data, "avg_temp_dp", "avg_tempdp_error");
        setAvgTempDP(next);
        updateFacilityCache(facilityID, { avgTempDP: next });
      } catch (err) {
        console.error("Error fetching Avg Temp data:", err);
        if (cancelled || !isMountedRef.current) return;
        setAvgTempDP((prev) => ({ ...prev, avg_temp_dp: null, avg_tempdp_error: true }));
      }
    };

    fetchAvgTempDP();
    const interval = startDashboardPolling(fetchAvgTempDP);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("avgHumDP", setAvgHumDP);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchAvgHumDP = async () => {
      try {
        const response = await axios.post(avgHumUrl(facilityID), null, createSkipAuthConfig());
        const data = response.data;

        if (cancelled || !isMountedRef.current) return;
        const next = normalizeAverageDeviationPayload(data, "avg_hum_dp", "avg_humdp_error");
        setAvgHumDP(next);
        updateFacilityCache(facilityID, { avgHumDP: next });
      } catch (err) {
        console.error("Error fetching Avg KPI data:", err);
        if (cancelled || !isMountedRef.current) return;
        setAvgHumDP((prev) => ({ ...prev, avg_hum_dp: null, avg_humdp_error: true }));
      }
    };

    fetchAvgHumDP();
    const interval = startDashboardPolling(fetchAvgHumDP);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("avgFTempDP", setAvgFTempDP);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchAvgFTempDP = async () => {
      try {
        const response = await axios.post(
          avgFloorTempUrl(facilityID),
          null,
          createSkipAuthConfig()
        );
        const data = response.data;

        if (cancelled || !isMountedRef.current) return;
        const next = normalizeAverageDeviationPayload(data, "avg_ftemp_dp", "avg_ftempdp_error");
        setAvgFTempDP(next);
        updateFacilityCache(facilityID, { avgFTempDP: next });
      } catch (err) {
        console.error("Error fetching Avg Temp data:", err);
        if (cancelled || !isMountedRef.current) return;
        setAvgFTempDP((prev) => ({ ...prev, avg_ftemp_dp: null, avg_ftempdp_error: true }));
      }
    };

    fetchAvgFTempDP();
    const interval = startDashboardPolling(fetchAvgFTempDP);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("avgNH3DP", setAvgNH3DP);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchAvgNH3DP = async () => {
      try {
        const response = await axios.post(avgNH3Url(facilityID), null, createSkipAuthConfig());
        const data = response.data;

        if (cancelled || !isMountedRef.current) return;
        const next = normalizeAverageDeviationPayload(data, "avg_nh3_dp", "avg_nh3dp_error");
        setAvgNH3DP(next);
        updateFacilityCache(facilityID, { avgNH3DP: next });
      } catch (err) {
        console.error("Error fetching Avg NH3 data:", err);
        if (cancelled || !isMountedRef.current) return;
        setAvgNH3DP((prev) => ({ ...prev, avg_nh3_dp: null, avg_nh3dp_error: true }));
      }
    };

    fetchAvgNH3DP();
    const interval = startDashboardPolling(fetchAvgNH3DP);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("avgKPIDP", setAvgKPIDP);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchAvgKPIDP = async () => {
      try {
        const response = await axios.post(
          avgCyclePerformanceKpiUrl(facilityID),
          null,
          createSkipAuthConfig()
        );

        if (cancelled || !isMountedRef.current) return;
        const next = {
          ...(Array.isArray(response?.data)
            ? response.data.reduce((acc, entry) => ({ ...acc, ...(entry || {}) }), {})
            : response?.data || {}),
          avg_fcrdp_error: false,
          avg_mortalitydp_error: false,
          avg_weightdp_error: false,
        };
        if (
          next &&
          typeof next.avg_weight_dp === "object" &&
          next.avg_weight_dp !== null &&
          !Array.isArray(next.avg_weight_dp) &&
          Object.keys(next.avg_weight_dp).length === 0
        ) {
          next.avg_weight_dp = null;
        }
        setAvgKPIDP(next);
        updateFacilityCache(facilityID, { avgKPIDP: next });
      } catch (err) {
        console.error("Error fetching Avg KPI data:", err);
        if (cancelled || !isMountedRef.current) return;
        setAvgKPIDP((prev) => ({
          ...prev,
          avg_fcr_dp: null,
          avg_mortality_dp: null,
          avg_weight_dp: null,
          avg_weightdp_message: null,
          avg_fcrdp_error: true,
          avg_mortalitydp_error: true,
          avg_weightdp_error: true,
        }));
      }
    };

    fetchAvgKPIDP();
    const interval = startDashboardPolling(fetchAvgKPIDP);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("avgLuxDP", setAvgLuxDP);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchAvgLuxDP = async () => {
      try {
        const response = await axios.post(avgLuxUrl(facilityID), null, createSkipAuthConfig());
        const data = response.data;

        if (cancelled || !isMountedRef.current) return;
        const next = normalizeAverageDeviationPayload(data, "avg_lux_dp", "avg_luxdp_error");
        setAvgLuxDP(next);
        updateFacilityCache(facilityID, { avgLuxDP: next });
      } catch (err) {
        console.error("Error fetching Avg Lighting data:", err);
        if (cancelled || !isMountedRef.current) return;
        setAvgLuxDP((prev) => ({ ...prev, avg_lux_dp: null, avg_luxdp_error: true }));
      }
    };

    fetchAvgLuxDP();
    const interval = startDashboardPolling(fetchAvgLuxDP);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("avgWaterTempDP", setAvgWaterTempDP);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchAvgWaterTempDP = async () => {
      try {
        const response = await axios.post(
          avgWaterTempUrl(facilityID),
          null,
          createSkipAuthConfig()
        );
        const data = response.data;

        if (cancelled || !isMountedRef.current) return;
        const next = normalizeAverageDeviationPayload(data, "avg_wtemp_dp", "avg_wtempdp_error");
        setAvgWaterTempDP(next);
        updateFacilityCache(facilityID, { avgWaterTempDP: next });
      } catch (err) {
        console.error("Error fetching Avg Water Temp data:", err);
        if (cancelled || !isMountedRef.current) return;
        setAvgWaterTempDP((prev) => ({ ...prev, avg_wtemp_dp: null, avg_wtempdp_error: true }));
      }
    };

    fetchAvgWaterTempDP();
    const interval = startDashboardPolling(fetchAvgWaterTempDP);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  /*useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }
    const fetchAvgWaterpHDP = async () => {
      try {
        const response = await axios.post(avgWaterPhUrl(facilityID), null, createSkipAuthConfig());
        const data = response.data;

        setAvgWaterpHDP(normalizeAverageDeviationPayload(data, "avg_ph_dp", "avg_phdp_error"));
      } catch (err) {
        console.error("Error fetching Avg Water pH data:", err);
      }
    };

    fetchAvgWaterpHDP();
    const interval = setInterval(fetchAvgWaterpHDP, 600000); // poll every 10m
    return () => clearInterval(interval); // cleanup
  }, [facilityID, navigate]);*/

  useEffect(() => {
    if (!facilityID) {
      const stored = JSON.parse(localStorage.getItem("broilers"));
      if (stored?.length > 0) {
        navigate(`/dashboard?facility=${stored[0].broilerID}`);
      }
    }

    const applyCached = () => {
      applyFacilityCached("avgActDP", setAvgActDP);
    };

    if (!canLoadDashboardData) return;

    if (isOffline) {
      applyCached();
      return;
    }

    let cancelled = false;

    const fetchAvgActDP = async () => {
      try {
        const response = await axios.post(avgActivityUrl(facilityID), null, createSkipAuthConfig());
        const data = response.data;

        if (cancelled || !isMountedRef.current) return;
        const next = { ...(data[0] || {}), avg_actdp_error: false };
        setAvgActDP(next);
        updateFacilityCache(facilityID, { avgActDP: next });
      } catch (err) {
        console.error("Error fetching Avg Activity data:", err);
        if (cancelled || !isMountedRef.current) return;
        setAvgActDP((prev) => ({ ...prev, avg_act_dp: null, avg_actdp_error: true }));
      }
    };

    fetchAvgActDP();
    const interval = startDashboardPolling(fetchAvgActDP);
    return () => {
      cancelled = true;
      stopDashboardPolling(interval);
    }; // cleanup
  }, [applyFacilityCached, facilityID, isOffline, navigate, refreshTick, updateFacilityCache]);

  // Mapping for health status to color
  const healthToColor = {
    red: "error",
    orange: "warning",
    green: "success",
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={3} pt={2}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6} lg={4}>
            <Typography variant="body1" color="text" sx={{ display: "block", mb: 1 }}>
              Farm:
            </Typography>
            <Select
              fullWidth
              value={selectedFarmId || ""}
              onChange={handleFarmChange}
              disabled={!farms.length}
              displayEmpty
              renderValue={(value) => {
                if (!value) return "Select Farm";
                const match = farms.find((farm) => farm.id === value);
                return match?.name || "Select Farm";
              }}
              sx={{
                height: "1cm",
                "& .MuiOutlinedInput-root, & .MuiInputBase-root": { height: "1cm !important" },
                "& .MuiSelect-select": {
                  minHeight: "1cm !important",
                  lineHeight: "1cm !important",
                  display: "flex",
                  alignItems: "center",
                  py: 0,
                },
              }}
            >
              <MenuItem value="" disabled>
                Select Farm
              </MenuItem>
              {farms.map((farm) => (
                <MenuItem key={farm.id} value={farm.id}>
                  {farm.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Typography variant="body1" color="text" sx={{ display: "block", mb: 1 }}>
              Facility:
            </Typography>
            <Select
              fullWidth
              value={facilityID || ""}
              onChange={handleFacilityChange}
              disabled={!filteredFacilities.length}
              displayEmpty
              renderValue={(value) => {
                if (!value) return "Select Facility";
                const match =
                  filteredFacilities.find((facility) => facility.id === value) ||
                  allFacilities.find((facility) => facility.id === value);
                return match?.name || "Select Facility";
              }}
              sx={{
                height: "1cm",
                "& .MuiOutlinedInput-root, & .MuiInputBase-root": { height: "1cm !important" },
                "& .MuiSelect-select": {
                  minHeight: "1cm !important",
                  lineHeight: "1cm !important",
                  display: "flex",
                  alignItems: "center",
                  py: 0,
                },
              }}
            >
              <MenuItem value="" disabled>
                Select Facility
              </MenuItem>
              {filteredFacilities.map((facility) => (
                <MenuItem key={facility.id} value={facility.id}>
                  {facility.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid
            item
            xs={12}
            lg={4}
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: { xs: "flex-start", lg: "flex-end" },
            }}
          >
            <Tooltip title={isOffline ? "Refresh unavailable while offline" : "Refresh dashboard"}>
              <span>
                <IconButton
                  onClick={handleDashboardRefresh}
                  disabled={!facilityID || isOffline}
                  sx={{
                    mt: { xs: 0, lg: 3.5 },
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                  }}
                >
                  <Icon>refresh</Icon>
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
        <Card
          sx={{
            height: { xs: "8.1cm", md: "4.4cm" },
            mt: "0.2cm",
            mb: { xs: "1cm", md: "1.3cm" },
            backgroundColor: "common.white",
            borderRadius: 2,
          }}
        >
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              height: "100%",
            }}
          >
            {[
              {
                heading: "Mortality",
                value: dashboardSummaryMetrics.rateMortality,
                suffix: "%",
                details: [
                  { label: "Mortality", value: dashboardSummaryMetrics.mortality },
                  { label: "Culls", value: dashboardSummaryMetrics.culls },
                  { label: "Live", value: dashboardSummaryMetrics.liveBirds },
                ],
              },
              {
                heading: "Weight",
                value: dashboardSummaryMetrics.avgWeight,
                suffix: "kg",
                details: [{ label: "Total", value: dashboardSummaryMetrics.totalWeight }],
              },
              {
                heading: "FCR",
                value: cardData?.fcr ?? dashboardSummaryMetrics.fcr,
                details: [{ label: "Feed", value: dashboardSummaryMetrics.totalFeed }],
              },
              {
                heading: "Water",
                value: dashboardSummaryMetrics.avgWater,
                details: [{ label: "Total", value: dashboardSummaryMetrics.totalWater }],
              },
            ].map((item) => (
              <MDBox
                key={item.heading}
                sx={{
                  p: 1.5,
                  display: "grid",
                  gridTemplateRows: "auto auto 1fr",
                  minWidth: 0,
                }}
              >
                <Typography variant="subtitle2" color="rgb(236, 132, 74)" fontWeight="regular">
                  {item.heading}
                </Typography>
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  sx={{ pt: "0.2cm" }}
                >
                  <Typography
                    sx={{
                      color: "#153E64",
                      fontSize: { xs: "2rem", md: "2.5rem" },
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {formatDashboardSummaryMetric(item.value, item.suffix)}
                  </Typography>
                </MDBox>
                {item.details ? (
                  <MDBox sx={{ display: "grid", pt: "0.4cm" }}>
                    {item.details.map((detail) => (
                      <MDBox key={detail.label} sx={{ lineHeight: 1, maxWidth: "3.6cm" }}>
                        <Typography
                          variant="subtitle2"
                          color="text.disabled"
                          fontWeight="regular"
                          lineHeight={1}
                          sx={{ whiteSpace: "pre" }}
                        >
                          {`${padDashboardDetailLabel(detail.label)}${formatDashboardSummaryMetric(
                            detail.value
                          )}`}
                        </Typography>
                      </MDBox>
                    ))}
                  </MDBox>
                ) : null}
              </MDBox>
            ))}
          </MDBox>
        </Card>
      </MDBox>
      <MDBox py={3}>
        {/* Complex Statistics Cards */}
        <Grid container spacing={3}></Grid>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MDBox mb={3} sx={{ height: "300px" }}>
                {cardData && barChartDataWeight && (
                  <WeightChart
                    color="secondary"
                    title="Weights"
                    cycle_day={cardData.cycle_day}
                    weight_average={cardData.weight_average}
                    placement_weight={cardData.placement_weight}
                    weight_target={cardData.weight_target}
                    weight_message={cardData.weight_message}
                    weight_status={cardData.weight_status}
                    timestamp={cardData.timestamp}
                    chart={barChartDataWeight}
                  />
                )}
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={3} sx={{ height: "300px" }}>
                {cardData && lineChartDataMortality && (
                  <MortalityChart
                    color="secondary"
                    title="Mortality (7 days)"
                    description="Daily Mortalities"
                    mortality_today={cardData.mortality_today}
                    mortality_target={cardData.mortality_target}
                    mortality_rate={cardData.mortality_rate}
                    mortality_message={cardData.mortality_message}
                    mortality_status={cardData.mortality_status}
                    live_count={cardData.live_count}
                    timestamp={cardData.timestamp}
                    chart={lineChartDataMortality}
                    chartHeight="12.5rem"
                  />
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MDBox mb={3} sx={{ height: "300px" }}>
                {cardData && feedChartData && (
                  <FeedChart
                    color="info"
                    title="Feed Consumption"
                    feed={cardData.feed}
                    feed_total={cardData.feed_total}
                    fcr={cardData.fcr}
                    fcr_day={cardData.fcr_day}
                    fcr_target={cardData.fcr_target}
                    fcr_message={cardData.fcr_message}
                    fcr_status={cardData.fcr_status}
                    fcr_deviationp={cardData.fcr_deviationp}
                    timestamp={cardData.timestamp}
                    chart={feedChartData}
                  />
                )}
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={3} sx={{ height: "300px" }}>
                {cardData && waterChartData && (
                  <WaterChart
                    color="info"
                    title="Water Consumption"
                    dates_test={waterChartData.labels}
                    water_usage={cardData.water_usage}
                    water_usage_test={waterChartData.datasets?.[0]?.data}
                    water_avgbird={cardData.water_avgbird}
                    water_total={cardData.water_total}
                    water_dailybird={cardData.water_dailybird}
                    water_avghour={cardData.water_avghour}
                    timestamp={cardData.timestamp}
                    chart={waterChartData}
                    chartHeight="12.5rem"
                  />
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MDBox mb={3} sx={{ height: "440px" }}>
                {cardData && envChartData && (
                  <EnvChart
                    color="dark"
                    title="Ambient Temp, Hum, NH3"
                    env_temp_sensor={cardData.env_temp_sensor}
                    env_temp_reading={cardData.env_temp_reading}
                    env_temp_status={cardData.env_temp_status}
                    env_temp_target={cardData.env_temp_target}
                    env_hum_sensor={cardData.env_hum_sensor}
                    env_hum_reading={cardData.env_hum_reading}
                    env_hum_status={cardData.env_hum_status}
                    env_hum_target={cardData.env_hum_target}
                    env_nh3_sensor={cardData.env_nh3_sensor}
                    env_nh3_reading={cardData.env_nh3_reading}
                    env_nh3_status={cardData.env_nh3_status}
                    env_nh3_target={cardData.env_nh3_target}
                    timestamp={cardData.timestamp}
                    chart={envChartData}
                    chartHeight="20rem"
                  />
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MDBox mb={3} sx={{ height: "440px" }}>
                {cardData && luxChartData && (
                  <LuxChart
                    color="secondary"
                    title="Lighting"
                    env_lux_sensor={cardData.env_lux_sensor}
                    env_lux_reading={cardData.env_lux_reading}
                    env_lux_status={cardData.env_lux_status}
                    env_lux_target={cardData.env_lux_target}
                    env_dark_reading={cardData.env_dark_reading}
                    env_dark_status={cardData.env_dark_status}
                    env_dark_target={cardData.env_dark_target}
                    timestamp={cardData.timestamp}
                    chart={luxChartData}
                    chartHeight="20rem"
                  />
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MDBox mb={3} sx={{ height: "440px" }}>
                {cardData && ftempChartData && (
                  <FloorTempChart
                    color="secondary"
                    title="Floor Temperature"
                    env_ftemp_sensor={cardData.env_ftemp_sensor}
                    env_ftemp_reading={cardData.env_ftemp_reading}
                    env_ftemp_status={cardData.env_ftemp_status}
                    env_ftemp_target={cardData.env_ftemp_target}
                    timestamp={cardData.timestamp}
                    chart={ftempChartData}
                    chartHeight="20rem"
                  />
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MDBox mb={3} sx={{ height: "440px" }}>
                {cardData && waterqChartData && (
                  <WaterQChart
                    color="info"
                    title="Water Quality"
                    waterq_temp_sensor={cardData.waterq_temp_sensor}
                    waterq_temp_reading={cardData.waterq_temp_reading}
                    waterq_temp_status={cardData.waterq_temp_status}
                    waterq_temp_target={cardData.waterq_temp_target}
                    waterq_ph_sensor={cardData.waterq_ph_sensor}
                    waterq_ph_reading={cardData.waterq_ph_reading}
                    waterq_ph_status={cardData.waterq_ph_status}
                    waterq_ph_target={cardData.waterq_ph_target}
                    timestamp={cardData.timestamp}
                    chart={waterqChartData}
                    chartHeight="20rem"
                  />
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {avgTempDP &&
                avgHumDP &&
                avgNH3DP &&
                avgLuxDP &&
                avgDarkDP &&
                avgKPIDP &&
                avgWaterTempDP &&
                //avgWaterpHDP &&
                avgActDP && (
                  <CycleDP
                    avg_temp_dp={avgTempDP.avg_temp_dp}
                    avg_tempdp_status={avgTempDP?.avg_tempdp_status}
                    avg_tempdp_error={avgTempDP?.avg_tempdp_error}
                    avg_hum_dp={avgHumDP?.avg_hum_dp}
                    avg_humdp_status={avgHumDP?.avg_humdp_status}
                    avg_humdp_error={avgHumDP?.avg_humdp_error}
                    avg_nh3_dp={avgNH3DP?.avg_nh3_dp}
                    avg_nh3dp_status={avgNH3DP?.avg_nh3dp_status}
                    avg_nh3dp_error={avgNH3DP?.avg_nh3dp_error}
                    avg_lux_dp={avgLuxDP?.avg_lux_dp}
                    avg_luxdp_status={avgLuxDP?.avg_luxdp_status}
                    avg_luxdp_error={avgLuxDP?.avg_luxdp_error}
                    avg_dark_dp={avgDarkDP?.avg_dark_dp}
                    avg_darkdp_status={avgDarkDP?.avg_darkdp_status}
                    avg_darkdp_error={avgDarkDP?.avg_darkdp_error}
                    avg_ftemp_dp={avgFTempDP.avg_ftemp_dp}
                    avg_ftempdp_status={avgFTempDP?.avg_ftempdp_status}
                    avg_ftempdp_error={avgFTempDP?.avg_ftempdp_error}
                    avg_fcr_dp={avgKPIDP?.avg_fcr_dp}
                    avg_fcrdp_status={avgKPIDP?.avg_fcrdp_status}
                    avg_fcrdp_error={avgKPIDP?.avg_fcrdp_error}
                    avg_mortality_dp={avgKPIDP?.avg_mortality_dp}
                    avg_mortalitydp_status={avgKPIDP?.avg_mortalitydp_status}
                    avg_mortalitydp_error={avgKPIDP?.avg_mortalitydp_error}
                    avg_weight_dp={avgKPIDP?.avg_weight_dp}
                    avg_weightdp_status={avgKPIDP?.avg_weightdp_status}
                    avg_weightdp_message={avgKPIDP?.avg_weightdp_message}
                    avg_weightdp_error={avgKPIDP?.avg_weightdp_error}
                    avg_wtemp_dp={avgWaterTempDP?.avg_wtemp_dp}
                    avg_wtempdp_status={avgWaterTempDP?.avg_wtempdp_status}
                    avg_wtempdp_error={avgWaterTempDP?.avg_wtempdp_error}
                    /*avg_ph_dp={avgWaterpHDP?.avg_ph_dp}
                    avg_phdp_status={avgWaterpHDP?.avg_phdp_status}*/
                    avg_act_dp={avgActDP?.avg_act_dp}
                    avg_actdp_status={avgActDP?.avg_actdp_status}
                    avg_actdp_error={avgActDP?.avg_actdp_error}
                    onMortalityClick={handleMortalityRedirect}
                  />
                )}
            </Grid>
            <Grid item xs={12} md={4}>
              {cardData && (
                <Activities
                  act_feed_message={cardData.act_feed_message}
                  act_feed_status={cardData.act_feed_status}
                  act_mortality_message={cardData.act_mortality_message}
                  act_mortality_status={cardData.act_mortality_status}
                  act_sanitation_message={cardData.act_sanitation_message}
                  act_sanitation_status={cardData.act_sanitation_status}
                  act_vaccination_message={cardData.act_vaccination_message}
                  act_vaccination_status={cardData.act_vaccination_status}
                  act_weight_message={cardData.act_weight_message}
                  act_weight_status={cardData.act_weight_status}
                  timestamp={cardData.timestamp}
                />
              )}
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Dialog
        open={Boolean(localInsightsMessage)}
        onClose={() => setLocalInsightsMessage("")}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Local Insights
          <IconButton
            aria-label="close"
            onClick={() => setLocalInsightsMessage("")}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text">
            {localInsightsMessage}
          </Typography>
        </DialogContent>
      </Dialog>
      <Footer />
      <Snackbar
        open={isOffline}
        message="Offline mode: showing cached dashboard data."
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={null}
      />
    </DashboardLayout>
  );
}

export default Dashboard;
